import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Icon, dateTimeTranslate, ReportNote as NYYReportNote } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import ReportCard from '../ReportCard';
import EditPlayerAuthoredNoteModal from './EditPlayerAuthoredNoteModal';
import ViewPlayerAuthoredNoteModal from './ViewPlayerAuthoredNoteModal';
import { Notes, useOktaQuery } from '../../data';
import usePlayerSelector from '../../utils/usePlayerSelector';
import { store } from '../../store';

const StyledNotesContainer = styled.div`
    text-align: left;
    padding: ${(props) => (props.compact ? '' : '10px')};
    margin: ${(props) => (props.compact ? '' : '5px')};
    h4 {
        font-size:18px;
        font-weight: bold;
    }
    * {
        border: none !important;
    }
`;

const PlayerAuthoredReportNote = ({ type, section, player, pitcher, batter, gameId, recordId,
    customTitle, customTitleForModal, customPlaceholder, compact, hideWhenNotFound, showSpanish,
    hasSpanishDataCallback, ...rest }) => {
    const { t: translate } = useTranslation();
    const { state: { user: loggedInUser } } = useContext(store);
    const [note, setNote] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [readMode, setReadMode] = useState(false);
    const modalContainer = useRef(null);
    const { PlayerID: userPlayerID } = usePlayerSelector();

    const queryNoteParams = { type, section, playerId: player?.PlayerID, pitcherId: pitcher?.PlayerID, batterId: batter?.PlayerID, gameid: gameId, recordid: recordId };
    const { isFetching } = useOktaQuery(
        ['reportNote', { ...queryNoteParams }],
        (t) => Notes.getReportNote(t, queryNoteParams),
        {
            onSuccess: (data) => {
                setNote({ ...data });
                if (hasSpanishDataCallback && data !== null && data.EsNote !== null) {
                    hasSpanishDataCallback(true);
                }
            },
        },
    );

    const addNote = () => {
        setNote({
            NoteID: 0,
            NoteType: type,
            NoteSection: section,
            PlayerID: player?.PlayerID,
            BatterID: batter?.PlayerID,
            PitcherID: pitcher?.PlayerID,
            Batter: batter,
            Pitcher: pitcher,
            GameID: gameId,
            RecordID: recordId,
        });
        setEditMode(true);
    };

    const cardTitle = () => {
        const titleText = customTitle || ((player?.PlayerID === userPlayerID) ? translate('components.notes.my-notes') : `${player?.FullName} ${translate('components.notes.notes')}`);
        return !compact && (
            <>
                {titleText}
                {!!note?.NoteID && note?.CanEdit && (
                    <Button className="float-right" variant="link" onClick={() => setEditMode(true)}>
                        <Icon icon="nyy-pencil" />
                    </Button>
                )}
            </>
        );
    };

    const allowAddNote = !window.$IsDugoutApp && player?.PlayerID === userPlayerID && loggedInUser.PlayerID === userPlayerID;
    if (hideWhenNotFound && !isFetching && !note?.NoteID && !allowAddNote) return <></>;

    return (
        <>
            <ReportCard
                title={cardTitle()}
                className={rest.className}
                onClick={() => { if (compact && note?.CanEdit) setEditMode(true); }}
                style={{ cursor: compact && note?.CanEdit ? 'pointer' : '' }}
            >
                <StyledNotesContainer compact={compact}>
                    <NYYReportNote isLoading={isFetching} note={note} showInfo={false} showSpanish={showSpanish} />
                    {!isFetching && !compact && !!note?.NoteID && (
                        <div className="mt-2 d-flex justify-content-between align-items-center">
                            <Button className="p-0" variant="link" onClick={() => setReadMode(true)}>{translate('components.notes.view-history')}</Button>
                            <span className="text-muted">{dateTimeTranslate(translate, note.LastUpdateDate)}</span>
                        </div>
                    )}
                    {!isFetching && !note?.NoteID && allowAddNote && (
                        <>
                            {compact && (
                                <div
                                    style={{ cursor: 'pointer' }}
                                    onClick={addNote}
                                >
                                    <b className="text-muted">{customPlaceholder ?? translate('components.notes.tap-to-add')}</b>
                                </div>
                            )}
                            {!compact && (
                                <div
                                    className="pt-5 pb-5 text-center"
                                    style={{ cursor: 'pointer' }}
                                    onClick={addNote}
                                >
                                    <h4 className="text-muted">{customPlaceholder ?? translate('components.notes.tap-to-add')}</h4>
                                </div>
                            )}
                        </>
                    )}
                    {!isFetching && !note?.NoteID && !allowAddNote && (
                        <>{translate('components.notes.no-player-notes-found')}</>
                    )}
                </StyledNotesContainer>
                <div ref={modalContainer} />
            </ReportCard>
            {editMode && (
                <EditPlayerAuthoredNoteModal
                    show={!!editMode}
                    container={modalContainer}
                    note={note}
                    onSave={(updatedNote) => { setNote({ ...updatedNote }); setEditMode(false); }}
                    onHide={() => setEditMode(false)}
                    customTitle={customTitleForModal}
                />
            )}
            {readMode && (
                <ViewPlayerAuthoredNoteModal
                    show={!!readMode}
                    container={modalContainer}
                    note={note}
                    onHide={() => setReadMode(false)}
                    customTitle={customTitleForModal}
                />
            )}
        </>
    );
};

PlayerAuthoredReportNote.propTypes = {
    type: PropTypes.string.isRequired,
    section: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    player: PropTypes.shape({ PlayerID: PropTypes.number, FullName: PropTypes.string }),
    pitcher: PropTypes.shape({ PlayerID: PropTypes.number, FullName: PropTypes.string }),
    batter: PropTypes.shape({ PlayerID: PropTypes.number, FullName: PropTypes.string }),
    gameId: PropTypes.number,
    recordId: PropTypes.number,
    customTitle: PropTypes.string,
    customTitleForModal: PropTypes.string,
    customPlaceholder: PropTypes.string,
    compact: PropTypes.bool,
    hideWhenNotFound: PropTypes.bool,
    showSpanish: PropTypes.bool,
    hasSpanishDataCallback: PropTypes.func,
};
PlayerAuthoredReportNote.defaultProps = {
    section: undefined,
    player: undefined,
    pitcher: undefined,
    batter: undefined,
    gameId: undefined,
    recordId: undefined,
    customTitle: undefined,
    customTitleForModal: undefined,
    customPlaceholder: undefined,
    compact: false,
    hideWhenNotFound: false,
    showSpanish: false,
    hasSpanishDataCallback: undefined,
};

export default PlayerAuthoredReportNote;
